/**
 * Rückgabemengen der Retoure: Stück ganzzahlig, Gewicht (kg) mit höchstens 3 Nachkommastellen.
 *
 * Gerechnet wird in ganzen Tausendsteln (Gramm bzw. 1/1000 Stück), damit 0,1 + 0,2 nie 0,30000…
 * ergibt. Eine Eingabe mit mehr als 3 Nachkommastellen wird **abgelehnt**, nie still gerundet.
 * Komma und Punkt sind als Dezimaltrenner erlaubt. Die Kasse rechnet damit nur Mengen, nie Beträge.
 */

/** Menge (Zahl vom Server bzw. aus der Auswahl) in Tausendsteln. */
export function tausendstel(menge: number): number {
  return Math.round(menge * 1000);
}

export function ausTausendsteln(t: number): number {
  return t / 1000;
}

export type MengenEingabe = { ok: true; menge: number } | { ok: false; fehler: string };

const MUSTER = /^(\d{1,6})(?:[.,](\d{1,3}))?$/;

/** Getippte Menge prüfen; `einheit` kg erlaubt 3 Nachkommastellen, Stück keine. */
export function leseMenge(roh: string, einheit: 'stk' | 'kg'): MengenEingabe {
  const t = roh.trim();
  if (t === '') {
    return { ok: false, fehler: 'Menge fehlt' };
  }
  if (einheit === 'kg' && /^\d+[.,]\d{4,}$/.test(t)) {
    return { ok: false, fehler: 'Höchstens 3 Nachkommastellen (z. B. 0,750)' };
  }
  const m = MUSTER.exec(t);
  if (m === null) {
    return {
      ok: false,
      fehler: einheit === 'kg' ? 'Gewicht wie 0,750 eingeben' : 'Ganze Stück eingeben',
    };
  }
  const ganz = Number(m[1]);
  const rest = m[2] ?? '';
  if (einheit === 'stk' && rest !== '' && Number(rest) !== 0) {
    return { ok: false, fehler: 'Ganze Stück eingeben' };
  }
  const t1000 = ganz * 1000 + Number(rest.padEnd(3, '0'));
  return { ok: true, menge: ausTausendsteln(t1000) };
}

/** Prüft eine gewählte Menge gegen die noch mögliche Menge (beides vom Server). */
export function mengeFehler(menge: number, moeglich: number, einheit: 'stk' | 'kg'): string | null {
  if (tausendstel(menge) <= 0) {
    return null;
  }
  if (tausendstel(menge) > tausendstel(moeglich)) {
    return `Menge zu hoch: ${mengeText(menge, einheit)} > ${mengeText(moeglich, einheit)} möglich`;
  }
  return null;
}

/** Anzeige: kg immer mit 3 Nachkommastellen und Komma, Stück ganzzahlig. */
export function mengeText(menge: number, einheit: 'stk' | 'kg'): string {
  if (einheit === 'kg') {
    const t = tausendstel(menge);
    const vorzeichen = t < 0 ? '-' : '';
    const a = Math.abs(t);
    return `${vorzeichen}${String(Math.floor(a / 1000))},${String(a % 1000).padStart(3, '0')} kg`;
  }
  return String(menge);
}

/** Schritt der −/+-Tasten: 1 Stück bzw. 0,250 kg (feiner über die Eingabe). */
export function mengenSchritt(einheit: 'stk' | 'kg'): number {
  return einheit === 'kg' ? 0.25 : 1;
}

/** Nächste Menge nach −/+, begrenzt auf 0 … möglich, in Tausendsteln gerechnet. */
export function schrittMenge(
  menge: number,
  richtung: 1 | -1,
  moeglich: number,
  einheit: 'stk' | 'kg',
): number {
  const neu = tausendstel(menge) + richtung * tausendstel(mengenSchritt(einheit));
  return ausTausendsteln(Math.min(tausendstel(moeglich), Math.max(0, neu)));
}
